import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Image,
  Text,
  Button,
  IconButton,
  Container,
  useDisclosure,
} from '@chakra-ui/react';
import { FaPhone, FaWhatsapp } from 'react-icons/fa';
import { AnimatePresence, motion, useScroll, useSpring } from 'framer-motion';
import { MotionBox, MotionFlex } from './motion';
import logoImage from '../Components/Assets/logo-mark.png';

const navLinks = [
  { name: 'Accueil', path: '/' },
  { name: 'À Propos', path: '/about' },
  { name: 'Agro Business', path: '/agro-business' },
  { name: 'Industries', path: '/industries' },
];

function Navbar() {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const location = useLocation();
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 26, restDelta: 0.001 });

  const isActive = (path) => location.pathname === path;
  const solid = scrolled || isOpen;

  /* Palette that flips with the navbar background */
  const colors = solid
    ? {
        navBg: 'rgba(255, 255, 255, 0.96)',
        navBorder: 'gray.100',
        link: 'gray.700',
        linkHover: 'brand.700',
        active: 'brand.700',
        activeBg: 'brand.50',
        hoverBg: 'gray.50',
        brand: 'brand.600',
        sub: 'gray.500',
        underline: 'brand.500',
      }
    : {
        navBg: 'transparent',
        navBorder: 'transparent',
        link: 'whiteAlpha.800',
        linkHover: 'white',
        active: 'white',
        activeBg: 'whiteAlpha.200',
        hoverBg: 'whiteAlpha.100',
        brand: 'gold.300',
        sub: 'whiteAlpha.700',
        underline: 'gold.300',
      };

  return (
    <>
      <Box
        as="nav"
        position="fixed"
        w="100%"
        top={0}
        left={0}
        zIndex={1000}
        bg={colors.navBg}
        backdropFilter={solid ? 'saturate(180%) blur(18px)' : 'none'}
        boxShadow={
          solid
            ? '0 1px 0 rgba(8,28,52,0.06), 0 18px 40px -24px rgba(8,28,52,0.45)'
            : 'none'
        }
        transition="background 0.4s cubic-bezier(0.4,0,0.2,1), box-shadow 0.4s ease, backdrop-filter 0.4s ease"
        borderBottom="1px solid"
        borderColor={colors.navBorder}
      >
        <Container maxW="7xl">
          <Flex justify="space-between" align="center" h={{ base: '72px', md: '80px' }}>
            {/* ─── Logo + Brand ─── */}
            <RouterLink to="/" aria-label="TRIMER SARL — Accueil" onClick={onClose}>
              <HStack spacing={3} transition="opacity 0.2s" _hover={{ opacity: 0.85 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  h={{ base: '44px', md: '48px' }}
                  w={{ base: '44px', md: '48px' }}
                  borderRadius="xl"
                  bg="white"
                  boxShadow={
                    solid
                      ? '0 4px 14px -6px rgba(8,28,52,0.35)'
                      : '0 4px 18px -6px rgba(0,0,0,0.45)'
                  }
                  flexShrink={0}
                  transition="box-shadow 0.4s"
                >
                  <Image
                    src={logoImage}
                    alt="TRIMER SARL"
                    h={{ base: '34px', md: '38px' }}
                    w="auto"
                    objectFit="contain"
                  />
                </Box>
                <VStack spacing={0} align="start" lineHeight="1.1">
                  <Text
                    fontSize={{ base: 'md', md: 'lg' }}
                    fontWeight="800"
                    letterSpacing="-0.02em"
                    color={solid ? 'navy.900' : 'white'}
                    transition="color 0.4s ease"
                  >
                    TRIMER{' '}
                    <Text as="span" color={colors.brand} transition="color 0.4s ease">
                      SARL
                    </Text>
                  </Text>
                  <Text
                    fontSize="10px"
                    fontWeight="600"
                    letterSpacing="0.18em"
                    textTransform="uppercase"
                    color={colors.sub}
                    transition="color 0.4s ease"
                    display={{ base: 'none', sm: 'block' }}
                  >
                    Agro-industrie · RDC
                  </Text>
                </VStack>
              </HStack>
            </RouterLink>

            {/* ─── Desktop Menu ─── */}
            <HStack spacing={1} display={{ base: 'none', lg: 'flex' }}>
              {navLinks.map((link) => {
                const active = isActive(link.path);
                return (
                  <Box
                    key={link.name}
                    as={RouterLink}
                    to={link.path}
                    position="relative"
                    px={4}
                    py={2.5}
                    borderRadius="lg"
                    bg={active ? colors.activeBg : 'transparent'}
                    color={active ? colors.active : colors.link}
                    fontSize="sm"
                    fontWeight={active ? '700' : '600'}
                    letterSpacing="0.01em"
                    transition="color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease"
                    _hover={{
                      color: colors.linkHover,
                      bg: active ? colors.activeBg : colors.hoverBg,
                    }}
                  >
                    {link.name}
                    {active && (
                      <MotionBox
                        layoutId="nav-active-pill"
                        position="absolute"
                        left={4}
                        right={4}
                        bottom={0}
                        h="3px"
                        borderRadius="full"
                        bg={colors.underline}
                        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                      />
                    )}
                  </Box>
                );
              })}

              <HStack spacing={3} ml={6}>
                <Button
                  as={RouterLink}
                  to="/contact"
                  size="md"
                  variant={solid ? 'solid' : 'ghostOnDark'}
                  bg={solid ? 'brand.600' : undefined}
                  _hover={solid ? { bg: 'brand.700', boxShadow: 'glow', transform: 'translateY(-2px)' } : undefined}
                  leftIcon={<FaPhone />}
                  px={6}
                >
                  Contact
                </Button>
                <Button
                  as="a"
                  href="https://wa.me/243810330830?text=Bonjour%20TRIMER%20SARL%20!"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="md"
                  variant={solid ? 'outline' : 'ghostOnDark'}
                  color={solid ? 'brand.600' : 'white'}
                  borderColor={solid ? 'brand.300' : 'whiteAlpha.400'}
                  bg={solid ? 'brand.50' : 'whiteAlpha.100'}
                  _hover={{
                    bg: solid ? 'brand.100' : 'whiteAlpha.300',
                    borderColor: solid ? 'brand.500' : 'whiteAlpha.700',
                    transform: 'translateY(-2px)',
                  }}
                  display={{ base: 'none', xl: 'inline-flex' }}
                  leftIcon={<FaWhatsapp />}
                  px={5}
                >
                  WhatsApp
                </Button>
              </HStack>
            </HStack>

            {/* ─── Mobile Toggle ─── */}
            <IconButton
              display={{ base: 'inline-flex', lg: 'none' }}
              onClick={onToggle}
              variant="ghost"
              aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              color={solid ? 'navy.900' : 'white'}
              _hover={{ bg: solid ? 'gray.100' : 'whiteAlpha.200' }}
              icon={
                <Box position="relative" w="22px" h="14px">
                  <MotionBox
                    position="absolute"
                    left={0}
                    w="22px"
                    h="2px"
                    bg="currentColor"
                    borderRadius="full"
                    animate={isOpen ? { top: '6px', rotate: 45 } : { top: '0px', rotate: 0 }}
                    transition="all 0.3s ease"
                  />
                  <MotionBox
                    position="absolute"
                    left={0}
                    w="22px"
                    h="2px"
                    bg="currentColor"
                    borderRadius="full"
                    animate={isOpen ? { top: '6px', rotate: -45 } : { top: '12px', rotate: 0 }}
                    transition="all 0.3s ease"
                  />
                </Box>
              }
            />
          </Flex>
        </Container>

        {/* ─── Scroll progress ─── */}
        <Box position="absolute" bottom={0} left={0} right={0} h="2px" bg="transparent">
          <motion.div
            style={{ scaleX: progress, transformOrigin: '0% 50%' }}
            className="nav-progress"
          />
        </Box>
      </Box>

      {/* ─── Mobile Drawer ─── */}
      <AnimatePresence>
        {isOpen && (
          <>
            <MotionBox
              position="fixed"
              inset={0}
              bg="blackAlpha.600"
              backdropFilter="blur(4px)"
              zIndex={999}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
            />
            <MotionFlex
              position="fixed"
              top={0}
              right={0}
              bottom={0}
              w="min(86vw, 380px)"
              bg="white"
              zIndex={1001}
              direction="column"
              boxShadow="-20px 0 60px -20px rgba(8,28,52,0.45)"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 34 }}
            >
              <Box p={6} borderBottom="1px solid" borderColor="gray.100">
                <Text fontSize="xs" fontWeight="700" letterSpacing="0.16em" textTransform="uppercase" color="brand.600">
                  Navigation
                </Text>
              </Box>

              <VStack align="stretch" spacing={1} p={5} flex="1">
                {navLinks.map((link) => (
                  <Box
                    key={link.name}
                    as={RouterLink}
                    to={link.path}
                    px={4}
                    py={3.5}
                    borderRadius="xl"
                    bg={isActive(link.path) ? 'brand.50' : 'transparent'}
                    borderLeft="3px solid"
                    borderColor={isActive(link.path) ? 'brand.500' : 'transparent'}
                    color={isActive(link.path) ? 'brand.700' : 'gray.700'}
                    fontWeight={isActive(link.path) ? '700' : '600'}
                    fontSize="sm"
                    letterSpacing="0.02em"
                    transition="all 0.2s"
                    _hover={{ bg: 'gray.50' }}
                  >
                    {link.name}
                  </Box>
                ))}
              </VStack>

              <VStack spacing={3} p={6} borderTop="1px solid" borderColor="gray.100">
                <Button as={RouterLink} to="/contact" w="100%" size="lg">
                  Nous contacter
                </Button>
                <Button
                  as="a"
                  href="https://wa.me/243810330830?text=Bonjour%20TRIMER%20SARL%20!"
                  target="_blank"
                  rel="noopener noreferrer"
                  w="100%"
                  size="lg"
                  variant="outline"
                  borderColor="brand.500"
                  color="brand.600"
                  leftIcon={<FaWhatsapp />}
                  _hover={{ bg: 'brand.50' }}
                >
                  WhatsApp
                </Button>
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  +243 836 944 316
                </Text>
              </VStack>
            </MotionFlex>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navbar;
